import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { X, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SaveToNotionModal({ result, onClose }) {
  const [includeTranscription, setIncludeTranscription] = useState(true);
  const [includeBullet, setIncludeBullet] = useState(true);
  const [includeStructured, setIncludeStructured] = useState(true);
  const [notionConnected, setNotionConnected] = useState(false);
  const [notionToken, setNotionToken] = useState("");
  const [databaseId, setDatabaseId] = useState("");
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [pageUrl, setPageUrl] = useState(null);

  useEffect(() => {
    checkNotionStatus();
  }, []);

  const checkNotionStatus = async () => {
    try {
      const response = await axios.get(`${API}/notion/status`, { withCredentials: true });
      setNotionConnected(response.data.connected);
    } catch (error) {
      console.error("Error checking Notion status:", error);
    }
  };

  const handleConnectNotion = async () => {
    if (!notionToken || !databaseId) {
      toast.error("Please enter both Notion token and database ID");
      return;
    }

    setConnecting(true);
    try {
      await axios.post(
        `${API}/notion/connect`,
        { notion_token: notionToken, database_id: databaseId },
        { withCredentials: true }
      );
      setNotionConnected(true);
      toast.success("Notion connected successfully!");
    } catch (error) {
      console.error("Error connecting Notion:", error);
      toast.error(error.response?.data?.detail || "Failed to connect Notion");
    } finally {
      setConnecting(false);
    }
  };

  const handleSave = async () => {
    if (!includeTranscription && !includeBullet && !includeStructured) {
      toast.error("Please select at least one item to save");
      return;
    }

    setSaving(true);
    setPageUrl(null);

    try {
      const response = await axios.post(
        `${API}/notion/save`,
        {
          video_title: result.video_title,
          platform: result.platform,
          include_transcription: includeTranscription,
          include_bullet_summary: includeBullet,
          include_structured_summary: includeStructured,
          transcription: result.transcription,
          bullet_summary: result.bullet_summary,
          structured_summary: result.structured_summary
        },
        { withCredentials: true }
      );

      setPageUrl(response.data.page_url);
      toast.success("Saved to Notion successfully!");
    } catch (error) {
      console.error("Error saving to Notion:", error);
      toast.error(error.response?.data?.detail || "Failed to save to Notion");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" data-testid="save-notion-modal">
      <div className="modal-content notion-modal">
        <div className="modal-header">
          <h2>Save to Notion</h2>
          <button onClick={onClose} className="close-button" data-testid="close-modal">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {!notionConnected ? (
            <div className="notion-setup">
              <p className="setup-instruction">Connect your Notion workspace to save content</p>
              
              <div className="form-group">
                <Label htmlFor="notion-token">Notion Integration Token</Label>
                <Input
                  id="notion-token"
                  type="password"
                  placeholder="secret_..."
                  value={notionToken}
                  onChange={(e) => setNotionToken(e.target.value)}
                  data-testid="notion-token-input"
                />
                <p className="input-hint">
                  <a href="https://www.notion.so/my-integrations" target="_blank" rel="noopener noreferrer">
                    Create an integration →
                  </a>
                </p>
              </div>

              <div className="form-group">
                <Label htmlFor="database-id">Database ID</Label>
                <Input
                  id="database-id"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={databaseId}
                  onChange={(e) => setDatabaseId(e.target.value)}
                  data-testid="database-id-input"
                />
                <p className="input-hint">
                  Copy from your Notion database URL
                </p>
              </div>

              <Button
                onClick={handleConnectNotion}
                disabled={connecting}
                className="connect-button"
                data-testid="connect-notion-button"
              >
                {connecting ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Connecting...
                  </>
                ) : (
                  "Connect Notion"
                )}
              </Button>
            </div>
          ) : (
            <>
              {pageUrl ? (
                <div className="success-state">
                  <div className="success-icon">✓</div>
                  <p className="success-message">Content saved to Notion!</p>
                  <a
                    href={pageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="notion-link"
                    data-testid="view-in-notion-link"
                  >
                    <ExternalLink size={16} />
                    View in Notion
                  </a>
                </div>
              ) : (
                <>
                  <p className="modal-description">
                    Select what to save to your Notion workspace
                  </p>

                  <div className="options-group">
                    <div className="option-item">
                      <Checkbox
                        id="include-transcription"
                        checked={includeTranscription}
                        onCheckedChange={setIncludeTranscription}
                        data-testid="include-transcription-checkbox"
                      />
                      <Label htmlFor="include-transcription" className="option-label">
                        Full Transcription
                      </Label>
                    </div>

                    {result.bullet_summary && (
                      <div className="option-item">
                        <Checkbox
                          id="include-bullet"
                          checked={includeBullet}
                          onCheckedChange={setIncludeBullet}
                          data-testid="include-bullet-checkbox"
                        />
                        <Label htmlFor="include-bullet" className="option-label">
                          Key Points Summary
                        </Label>
                      </div>
                    )}

                    {result.structured_summary && (
                      <div className="option-item">
                        <Checkbox
                          id="include-structured"
                          checked={includeStructured}
                          onCheckedChange={setIncludeStructured}
                          data-testid="include-structured-checkbox"
                        />
                        <Label htmlFor="include-structured" className="option-label">
                          Structured Analysis
                        </Label>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="save-button"
                    data-testid="save-to-notion-button"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Saving...
                      </>
                    ) : (
                      "Save to Notion"
                    )}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SaveToNotionModal;
